import DeleteIcon from '@mui/icons-material/Delete'
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import { useDeleteApprovedRemoteException } from '@src/api/exception-api'
import { usePaginationParams } from '@src/services/paginationhook'
import {
  ApprovedRemoteExceptions,
  ApprovedRemoteExceptionsParams,
  ApprovedRemoteExceptionsQueryParams,
  PaginationParams,
} from '@src/types/ApprovedRemoteExceptions'
import { Person } from '@src/types/Person'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface ExceptionDataTableProps {
  persons: Person[]
  exceptions: ApprovedRemoteExceptions[]
  paginationParams: PaginationParams
  onPaginationParamsChange: (model: PaginationParams) => void
  queryParams: ApprovedRemoteExceptionsQueryParams
  isFetching: boolean
}

const ExceptionDataTable = ({
  persons,
  exceptions,
  paginationParams,
  onPaginationParamsChange,
  queryParams,
  isFetching,
}: ExceptionDataTableProps) => {
  const [deleteApprovedRemoteException] = useDeleteApprovedRemoteException(queryParams)
  const { t } = useTranslation()
  const columns = useMemo<GridColDef<ApprovedRemoteExceptions>[]>(
    () => [
      {
        field: 'date',
        headerName: t('approvedRemoteExceptions.date'),
        width: 200,
        flex: 1.5,
        sortable: true,
      },
      {
        field: 'personFullName',
        headerName: t('approvedRemoteExceptions.personName'),
        width: 400,
        flex: 3,
        sortable: false,
      },
      {
        field: 'reason',
        headerName: t('approvedRemoteExceptions.reason'),
        width: 300,
        flex: 2,
        sortable: true,
      },
      {
        field: 'actions',
        type: 'actions',
        getActions: (params) => [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon color="error" />}
            label="Delete"
            onClick={async () => {
              const Requestparams: ApprovedRemoteExceptionsParams = {
                id: params.row.id!,
                partitionKey: params.row.personId,
              }
              await deleteApprovedRemoteException(Requestparams)
            }}
          />,
        ],
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
      },
    ],
    [deleteApprovedRemoteException, t],
  )

  const personMap = useMemo(() => {
    const map = new Map<string, string>()
    persons.forEach((person) => {
      map.set(person.id, person.fullName)
    })
    return map
  }, [persons])

  const rows = useMemo(() => {
    return exceptions.map((exception) => ({
      ...exception,
      personFullName: personMap.get(exception.personId) || 'unknown',
    }))
  }, [exceptions, personMap])

  const dataGridProps = usePaginationParams(rows, isFetching, paginationParams, onPaginationParamsChange)
  return <DataGrid {...dataGridProps} rows={rows} columns={columns} pageSizeOptions={[100]} />
}

export default ExceptionDataTable
